import React, { createContext, useContext, useState, useCallback, useEffect, useRef } from 'react';
import type { LeaveRequest, OvertimeRequest, SalaryAdvanceRequest, ShiftSwapRequest } from '../types';
import { useAuth } from './AuthContext';
import * as requestsService from '../services/requests';
import type { SubmitLeavePayload, SubmitOvertimePayload, SubmitSalaryAdvancePayload, SubmitShiftSwapPayload } from '../services/requests';

interface RequestsState {
  leaveRequests: LeaveRequest[];
  overtimeRequests: OvertimeRequest[];
  salaryAdvanceRequests: SalaryAdvanceRequest[];
  shiftSwapRequests: ShiftSwapRequest[];
  loading: boolean;
}

interface RequestsContextValue extends RequestsState {
  submitLeave: (payload: SubmitLeavePayload) => Promise<void>;
  cancelLeave: (id: string) => Promise<void>;
  submitOvertime: (payload: SubmitOvertimePayload) => Promise<void>;
  cancelOvertime: (id: string) => Promise<void>;
  submitSalaryAdvance: (payload: SubmitSalaryAdvancePayload) => Promise<void>;
  cancelSalaryAdvance: (id: string) => Promise<void>;
  submitShiftSwap: (payload: SubmitShiftSwapPayload) => Promise<void>;
  cancelShiftSwap: (id: string) => Promise<void>;
  /**
   * Lazy-load all request types.
   * Skips the network calls if already loaded (unless force=true).
   * Pages call this in their own useEffect — only Requests and Profile need it.
   */
  refreshAll: (force?: boolean) => Promise<void>;
}

const RequestsContext = createContext<RequestsContextValue | null>(null);

export function RequestsProvider({ children }: { children: React.ReactNode }): React.JSX.Element {
  const { isAuthenticated } = useAuth();
  const [state, setState] = useState<RequestsState>({
    leaveRequests: [],
    overtimeRequests: [],
    salaryAdvanceRequests: [],
    shiftSwapRequests: [],
    loading: false,
  });

  // Ref-based guard: stable reference, no re-render on flag toggle.
  const loadedRef = useRef(false);

  // On logout: clear state and reset the loaded flag.
  useEffect(() => {
    if (!isAuthenticated) {
      loadedRef.current = false;
      setState({ leaveRequests: [], overtimeRequests: [], salaryAdvanceRequests: [], shiftSwapRequests: [], loading: false });
    }
  }, [isAuthenticated]);

  const refreshAll = useCallback(async (force = false): Promise<void> => {
    if (!isAuthenticated) return;
    if (loadedRef.current && !force) return;
    setState((s) => ({ ...s, loading: true }));
    try {
      const [leave, overtime, salary, shiftSwap] = await Promise.all([
        requestsService.getLeaveRequests(),
        requestsService.getOvertimeRequests(),
        requestsService.getSalaryAdvanceRequests(),
        requestsService.getShiftSwapRequests(),
      ]);
      setState({ leaveRequests: leave, overtimeRequests: overtime, salaryAdvanceRequests: salary, shiftSwapRequests: shiftSwap, loading: false });
      loadedRef.current = true;
    } catch {
      setState((s) => ({ ...s, loading: false }));
    }
  }, [isAuthenticated]);

  const submitLeave = useCallback(async (payload: SubmitLeavePayload): Promise<void> => {
    await requestsService.submitLeaveRequest(payload);
    await refreshAll(true);
  }, [refreshAll]);

  const cancelLeave = useCallback(async (id: string): Promise<void> => {
    await requestsService.cancelLeaveRequest(id);
    await refreshAll(true);
  }, [refreshAll]);

  const submitOvertime = useCallback(async (payload: SubmitOvertimePayload): Promise<void> => {
    await requestsService.submitOvertimeRequest(payload);
    await refreshAll(true);
  }, [refreshAll]);

  const cancelOvertime = useCallback(async (id: string): Promise<void> => {
    await requestsService.cancelOvertimeRequest(id);
    await refreshAll(true);
  }, [refreshAll]);

  const submitSalaryAdvance = useCallback(async (payload: SubmitSalaryAdvancePayload): Promise<void> => {
    await requestsService.submitSalaryAdvanceRequest(payload);
    await refreshAll(true);
  }, [refreshAll]);

  const cancelSalaryAdvance = useCallback(async (id: string): Promise<void> => {
    await requestsService.cancelSalaryAdvanceRequest(id);
    await refreshAll(true);
  }, [refreshAll]);

  const submitShiftSwap = useCallback(async (payload: SubmitShiftSwapPayload): Promise<void> => {
    await requestsService.submitShiftSwapRequest(payload);
    await refreshAll(true);
  }, [refreshAll]);

  const cancelShiftSwap = useCallback(async (id: string): Promise<void> => {
    await requestsService.cancelShiftSwapRequest(id);
    await refreshAll(true);
  }, [refreshAll]);

  return (
    <RequestsContext.Provider
      value={{ ...state, submitLeave, cancelLeave, submitOvertime, cancelOvertime, submitSalaryAdvance, cancelSalaryAdvance, submitShiftSwap, cancelShiftSwap, refreshAll }}
    >
      {children}
    </RequestsContext.Provider>
  );
}

export function useRequests(): RequestsContextValue {
  const ctx = useContext(RequestsContext);
  if (!ctx) throw new Error('useRequests must be used within RequestsProvider');
  return ctx;
}

